import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Link as MuiLink,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import HomeIcon from '@mui/icons-material/Home';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import { Link as RouterLink } from 'react-router-dom';
import useIsMobile from '../../hooks/useIsMobile';
import { RevaAction, RevaActionObject } from '../../api/services/chatbot';

/** Where a card is in the confirm flow. Only `pending` shows controls. */
export type RevaActionState =
  | 'pending'
  | 'working'
  | 'confirmed'
  | 'failed'
  | 'cancelled'
  | 'changing'
  /** Superseded by something the buyer typed instead — Reva's next message says what happened. */
  | 'closed';

const KIND_ICONS: Record<string, React.ElementType> = {
  schedule_showing: EventAvailableIcon,
  connect_with_agent: SupportAgentIcon,
};

const OBJECT_ICONS: Record<string, React.ElementType> = {
  listing: HomeIcon,
  offer: ReceiptLongIcon,
  transaction: ReceiptLongIcon,
  tour: EventAvailableIcon,
  agent: SupportAgentIcon,
};

const STATUS_LABELS: Record<RevaActionState, string | null> = {
  pending: null,
  working: 'Requesting…',
  confirmed: 'Requested',
  failed: 'Not completed',
  cancelled: 'Cancelled',
  changing: 'Editing',
  closed: null,
};

/** The object header shared by action cards and read-only citation cards. */
const ObjectHeader: React.FC<{
  object: RevaActionObject;
  compact?: boolean;
}> = ({ object, compact = false }) => {
  const theme = useTheme();
  const Icon = OBJECT_ICONS[object.type] ?? HomeIcon;

  return (
    <Stack
      direction="row"
      spacing={1.5}
      alignItems="center"
      sx={{ minWidth: 0 }}
    >
      {object.photo ? (
        <Box
          component="img"
          src={object.photo}
          alt={object.title}
          sx={{
            width: compact ? 48 : 56,
            height: compact ? 48 : 56,
            borderRadius: 1.5,
            objectFit: 'cover',
            flexShrink: 0,
          }}
        />
      ) : (
        <Box
          sx={{
            width: compact ? 48 : 56,
            height: compact ? 48 : 56,
            borderRadius: 1.5,
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: `${theme.palette.primary.main}12`,
            color: theme.palette.primary.main,
          }}
        >
          <Icon sx={{ fontSize: 24 }} />
        </Box>
      )}

      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          variant="body2"
          fontWeight={600}
          color="text.primary"
          noWrap
          title={object.title}
        >
          {object.title}
        </Typography>
        {object.subtitle && (
          <Typography
            variant="caption"
            color="text.secondary"
            noWrap
            display="block"
          >
            {object.subtitle}
          </Typography>
        )}
        <Stack
          direction="row"
          spacing={1}
          flexWrap="wrap"
          useFlexGap
          sx={{ mt: 0.25 }}
        >
          {object.priceLabel && (
            <Typography
              variant="caption"
              sx={{ fontWeight: 700, color: 'primary.main' }}
            >
              {object.priceLabel}
            </Typography>
          )}
          {object.facts.map((fact) => (
            <Typography key={fact} variant="caption" color="text.secondary">
              {fact}
            </Typography>
          ))}
        </Stack>
      </Box>

      {object.href && (
        <MuiLink
          component={RouterLink}
          to={object.href}
          underline="none"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            fontSize: '0.75rem',
            fontWeight: 600,
            color: 'primary.main',
            flexShrink: 0,
          }}
        >
          Open
          <OpenInNewIcon sx={{ fontSize: 14 }} />
        </MuiLink>
      )}
    </Stack>
  );
};

/** An object Reva cited, with no controls — the read-only half of the same card language. */
export const RevaObjectCard: React.FC<{ object: RevaActionObject }> = ({
  object,
}) => {
  const theme = useTheme();

  return (
    <Card
      elevation={0}
      data-testid="reva-object-card"
      sx={{
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
      }}
    >
      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
        <ObjectHeader object={object} compact />
        {object.status && (
          <Chip
            label={object.status}
            size="small"
            sx={{
              mt: 1,
              height: 22,
              fontSize: '0.7rem',
              fontWeight: 600,
              color: theme.palette.primary.main,
              backgroundColor: `${theme.palette.primary.main}12`,
            }}
          />
        )}
      </CardContent>
    </Card>
  );
};

interface RevaActionCardProps {
  action: RevaAction;
  state: RevaActionState;
  /** What the tool reported once it ran, in its own words. */
  resultMessage?: string | null;
  onConfirm: () => void;
  onChange: () => void;
  onCancel: () => void;
}

/**
 * The confirm surface for a side effect Reva proposed. The buyer's own tap on "Confirm" is the
 * only thing that runs it — the card never acts on its own, and it goes inert the moment the
 * action is resolved one way or the other.
 */
const RevaActionCard: React.FC<RevaActionCardProps> = ({
  action,
  state,
  resultMessage = null,
  onConfirm,
  onChange,
  onCancel,
}) => {
  const theme = useTheme();
  const isMobile = useIsMobile();
  const Icon = KIND_ICONS[action.kind] ?? AutoAwesomeIcon;
  const statusLabel = STATUS_LABELS[state];
  const showControls = state === 'pending' || state === 'working';
  const busy = state === 'working';

  return (
    <Card
      elevation={0}
      data-testid="reva-action-card"
      data-action-state={state}
      data-action-kind={action.kind}
      sx={{
        width: '100%',
        borderRadius: 2.5,
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
      }}
    >
      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Box
            sx={{
              p: 0.75,
              borderRadius: 1,
              display: 'flex',
              backgroundColor: `${theme.palette.primary.main}12`,
              color: theme.palette.primary.main,
            }}
          >
            <Icon sx={{ fontSize: 20 }} />
          </Box>
          <Typography
            variant="caption"
            sx={{
              flex: 1,
              color: theme.palette.primary.main,
              fontWeight: 600,
              letterSpacing: 0.6,
              textTransform: 'uppercase',
              lineHeight: 1.3,
            }}
          >
            {action.title}
          </Typography>
          {statusLabel && (
            <Chip
              label={statusLabel}
              size="small"
              data-testid="reva-action-status"
              icon={
                state === 'confirmed' ? (
                  <CheckCircleIcon sx={{ fontSize: 14 }} />
                ) : undefined
              }
              sx={{
                height: 22,
                fontSize: '0.7rem',
                fontWeight: 600,
                color: 'text.secondary',
                backgroundColor: theme.palette.action.hover,
                '& .MuiChip-icon': { color: theme.palette.success.main },
              }}
            />
          )}
        </Stack>

        <Typography
          variant="body2"
          color="text.primary"
          sx={{ mt: 0.75, fontWeight: 600, lineHeight: 1.45 }}
        >
          {action.prompt}
        </Typography>

        {action.object && (
          <Box sx={{ mt: 1 }}>
            <ObjectHeader object={action.object} compact />
          </Box>
        )}

        {action.params.length > 0 && (
          <>
            <Divider sx={{ my: 1 }} />
            <Stack spacing={0.5}>
              {action.params.map((param) => (
                <Stack
                  key={param.key}
                  direction="row"
                  justifyContent="space-between"
                  spacing={2}
                >
                  <Typography variant="caption" color="text.secondary">
                    {param.label}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.primary"
                    fontWeight={600}
                    sx={{ textAlign: 'right' }}
                  >
                    {param.value}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </>
        )}

        {resultMessage && !showControls && (
          <Typography
            variant="caption"
            color="text.secondary"
            data-testid="reva-action-result"
            sx={{ mt: 1, display: 'block' }}
          >
            {resultMessage}
          </Typography>
        )}

        {showControls && (
          <Stack
            direction={isMobile ? 'column' : 'row'}
            spacing={1}
            sx={{ mt: 1.25 }}
            alignItems={isMobile ? 'stretch' : 'center'}
          >
            <Button
              variant="contained"
              size="small"
              disableElevation
              disabled={busy}
              onClick={onConfirm}
              fullWidth={isMobile}
              data-testid="reva-action-confirm"
              startIcon={
                busy ? (
                  <CircularProgress size={14} color="inherit" />
                ) : undefined
              }
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
            >
              {action.confirmLabel}
            </Button>
            <Button
              variant="outlined"
              size="small"
              disabled={busy}
              onClick={onChange}
              fullWidth={isMobile}
              data-testid="reva-action-change"
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
            >
              Change
            </Button>
            <Button
              variant="text"
              size="small"
              color="inherit"
              disabled={busy}
              onClick={onCancel}
              fullWidth={isMobile}
              data-testid="reva-action-cancel"
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                color: 'text.secondary',
              }}
            >
              Cancel
            </Button>
          </Stack>
        )}
      </CardContent>
    </Card>
  );
};

export default RevaActionCard;
